import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { TranslationService } from '@/layout/service/translation.service';
import {
    LocationWorkingHoursResponse,
    LocationWorkingHoursUpdate,
    ResolvedWorkingHours,
    WorkingDayRow,
    WorkingHoursSource,
    describeWorkingHours,
    timeZoneOptions,
    weekdayNames,
    workingDayRows,
    workingHoursFromRows
} from '@/cloud/models/working-hours.models';
import { WorkingDaysEditorComponent } from './working-days-editor.component';

export type HoursMode = 'inherit' | 'custom' | 'all';
export type ZoneMode = 'inherit' | 'custom';

/** The dialog's state for a stored override (or none). */
export function overrideForm(response: LocationWorkingHoursResponse): { hoursMode: HoursMode; zoneMode: ZoneMode; timeZone: string; rows: WorkingDayRow[] } {
    const own = response.override;
    const hoursMode: HoursMode = own?.allHours ? 'all' : own?.days?.length ? 'custom' : 'inherit';
    const inheritedDays = response.inherited.days;
    const startFrom = own?.days?.length ? own.days : inheritedDays;
    return {
        hoursMode,
        zoneMode: own?.timeZone ? 'custom' : 'inherit',
        timeZone: own?.timeZone ?? response.inherited.timeZone,
        rows: workingDayRows(startFrom ? { timeZone: response.inherited.timeZone, days: startFrom } : null)
    };
}

/**
 * The request for the dialog's state: null means inherit everything (DELETE the override); a string is an error key.
 */
export function overrideRequest(hoursMode: HoursMode, zoneMode: ZoneMode, timeZone: string, rows: WorkingDayRow[]): LocationWorkingHoursUpdate | null | 'workingHours.noDays' | 'workingHours.invalid' {
    const zone = zoneMode === 'custom' ? timeZone : null;
    if (hoursMode === 'inherit' && !zone) return null;
    if (hoursMode === 'custom') {
        const hours = workingHoursFromRows(zone ?? 'UTC', rows);
        if (typeof hours === 'string') return hours;
        return { timeZone: zone, days: hours.days, allHours: false };
    }
    return { timeZone: zone, days: null, allHours: hoursMode === 'all' };
}

/**
 * Working hours for a location (building, floor or room) on its Overview: what is in effect and where each part comes
 * from, with an editor to override the hours, make every hour count, or set a time zone. Locations below inherit it.
 */
@Component({
    selector: 'app-location-working-hours',
    standalone: true,
    imports: [FormsModule, ButtonModule, DialogModule, MessageModule, RadioButtonModule, SelectModule, WorkingDaysEditorComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (data(); as d) {
            <dl class="lwh">
                <div class="lwh__row">
                    <dt>{{ t()('locationHours.hours') }}</dt>
                    <dd>
                        <span class="lwh__value">{{ hoursText(d.effective) }}</span>
                        <span class="lwh__source">{{ sourceText(d.effective.hoursFrom) }}</span>
                    </dd>
                </div>
                <div class="lwh__row">
                    <dt>{{ t()('workingHours.timeZone') }}</dt>
                    <dd>
                        <span class="lwh__value">{{ d.effective.timeZone }}</span>
                        <span class="lwh__source">{{ sourceText(d.effective.timeZoneFrom) }}</span>
                    </dd>
                </div>
            </dl>
            @if (canManage()) {
                <p-button class="lwh__edit" [label]="t()('locationHours.edit')" icon="pi pi-pencil" size="small" [text]="true" (onClick)="open()" />
            }
        } @else if (loadFailed()) {
            <p class="lwh__muted">{{ t()('workingHours.loadError') }}</p>
        } @else {
            <p class="lwh__muted">…</p>
        }

        <p-dialog [header]="t()('locationHours.title')" [visible]="editing()" (visibleChange)="!$event && close()" [modal]="true" [closable]="!saving()" [draggable]="false" [style]="{ width: 'min(40rem, calc(100vw - 2rem))' }" appendTo="body">
            @if (data(); as d) {
                <p class="lwh__intro">
                    <strong>{{ locationName() }}</strong> · {{ t()('locationHours.intro') }}
                </p>

                <fieldset class="lwh__group">
                    <legend>{{ t()('locationHours.hours') }}</legend>
                    <div class="lwh__option">
                        <p-radiobutton inputId="lwh-hours-inherit" name="hoursMode" value="inherit" [ngModel]="hoursMode()" (ngModelChange)="hoursMode.set($event)" />
                        <label for="lwh-hours-inherit"
                            >{{ t()('locationHours.inherit') }} <span class="lwh__muted">({{ hoursText(d.inherited) }} · {{ sourceText(d.inherited.hoursFrom) }})</span></label
                        >
                    </div>
                    <div class="lwh__option">
                        <p-radiobutton inputId="lwh-hours-custom" name="hoursMode" value="custom" [ngModel]="hoursMode()" (ngModelChange)="hoursMode.set($event)" />
                        <label for="lwh-hours-custom">{{ t()('locationHours.custom') }}</label>
                    </div>
                    @if (hoursMode() === 'custom') {
                        <app-working-days-editor class="lwh__editor" idPrefix="lwh" [rows]="rows()" (rowsChange)="rows.set($event)" [label]="t()('locationHours.custom')" />
                    }
                    <div class="lwh__option">
                        <p-radiobutton inputId="lwh-hours-all" name="hoursMode" value="all" [ngModel]="hoursMode()" (ngModelChange)="hoursMode.set($event)" />
                        <label for="lwh-hours-all"
                            >{{ t()('locationHours.allHours') }} <span class="lwh__muted">({{ t()('locationHours.allHoursHint') }})</span></label
                        >
                    </div>
                </fieldset>

                <fieldset class="lwh__group">
                    <legend>{{ t()('workingHours.timeZone') }}</legend>
                    <div class="lwh__option">
                        <p-radiobutton inputId="lwh-zone-inherit" name="zoneMode" value="inherit" [ngModel]="zoneMode()" (ngModelChange)="zoneMode.set($event)" />
                        <label for="lwh-zone-inherit"
                            >{{ t()('locationHours.inherit') }} <span class="lwh__muted">({{ d.inherited.timeZone }} · {{ sourceText(d.inherited.timeZoneFrom) }})</span></label
                        >
                    </div>
                    <div class="lwh__option">
                        <p-radiobutton inputId="lwh-zone-custom" name="zoneMode" value="custom" [ngModel]="zoneMode()" (ngModelChange)="zoneMode.set($event)" />
                        <label for="lwh-zone-custom">{{ t()('locationHours.setZone') }}</label>
                    </div>
                    @if (zoneMode() === 'custom') {
                        <p-select class="w-full lwh__zone" [options]="zones()" [ngModel]="timeZone()" (ngModelChange)="timeZone.set($event)" [filter]="true" appendTo="body" [ariaLabel]="t()('workingHours.timeZone')" />
                    }
                </fieldset>

                @if (error(); as key) {
                    <p-message severity="error">{{ t()(key) }}</p-message>
                }
            }
            <ng-template #footer>
                <div class="lwh__footer">
                    @if (data()?.override) {
                        <p-button [label]="t()('locationHours.reset')" severity="secondary" [text]="true" (onClick)="reset()" [disabled]="saving()" />
                    }
                    <span class="lwh__spacer"></span>
                    <p-button [label]="t()('locationHours.cancel')" severity="secondary" [outlined]="true" (onClick)="close()" [disabled]="saving()" />
                    <p-button [label]="t()('locationHours.save')" icon="pi pi-check" (onClick)="save()" [loading]="saving()" [disabled]="saving() || !!error()" />
                </div>
            </ng-template>
        </p-dialog>
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }
            .lwh {
                margin: 0;
                display: grid;
                gap: 0.75rem;
            }
            .lwh__row dt {
                font-size: 0.75rem;
                color: var(--text-color-secondary);
                margin-bottom: 0.125rem;
            }
            .lwh__row dd {
                margin: 0;
                display: flex;
                flex-direction: column;
            }
            .lwh__value {
                font-weight: 600;
            }
            .lwh__source,
            .lwh__muted {
                font-size: 0.8125rem;
                color: var(--text-color-secondary);
            }
            .lwh__edit {
                align-self: flex-start;
            }
            .lwh__intro {
                margin: 0 0 1rem;
                color: var(--text-color-secondary);
            }
            .lwh__group {
                border: 0;
                padding: 0;
                margin: 0 0 1.25rem;
                display: grid;
                gap: 0.625rem;
            }
            .lwh__group legend {
                font-weight: 600;
                margin-bottom: 0.5rem;
                padding: 0;
            }
            .lwh__option {
                display: flex;
                gap: 0.5rem;
                align-items: center;
            }
            .lwh__editor,
            .lwh__zone {
                margin-left: 1.75rem;
            }
            .lwh__footer {
                display: flex;
                gap: 0.5rem;
                width: 100%;
                flex-wrap: wrap;
            }
            .lwh__spacer {
                flex: 1;
            }
            @media (max-width: 30rem) {
                .lwh__editor,
                .lwh__zone {
                    margin-left: 0;
                }
            }
        `
    ]
})
export class LocationWorkingHoursComponent {
    private api = inject(CloudApiService);
    private messages = inject(MessageService);
    private translation = inject(TranslationService);
    readonly t = this.translation.t;

    readonly locationId = input.required<string>();
    readonly locationName = input('');
    readonly canManage = input(false);

    readonly data = signal<LocationWorkingHoursResponse | null>(null);
    readonly loadFailed = signal(false);
    readonly editing = signal(false);
    readonly saving = signal(false);
    readonly hoursMode = signal<HoursMode>('inherit');
    readonly zoneMode = signal<ZoneMode>('inherit');
    readonly timeZone = signal('UTC');
    readonly rows = signal<WorkingDayRow[]>(workingDayRows(null));

    readonly zones = computed(() => timeZoneOptions(this.timeZone()));
    private readonly request = computed(() => overrideRequest(this.hoursMode(), this.zoneMode(), this.timeZone(), this.rows()));
    readonly error = computed(() => {
        const r = this.request();
        return typeof r === 'string' ? r : null;
    });
    private readonly dayNames = computed(() => weekdayNames(this.translation.currentLanguage()));
    private loads = 0;

    constructor() {
        effect(() => {
            const id = this.locationId();
            untracked(() => void this.load(id));
        });
    }

    hoursText(resolved: ResolvedWorkingHours): string {
        return resolved.days?.length ? describeWorkingHours(resolved.days, this.dayNames()) : this.t()('locationHours.everyHour');
    }

    sourceText(source: WorkingHoursSource): string {
        if (source.kind === 'tenant') return this.t()('locationHours.fromTenant');
        if (source.kind === 'none') return this.t()('locationHours.notSet');
        if (source.locationId === this.locationId()) return this.t()('locationHours.setHere');
        return this.t()('locationHours.fromLocation').replace('{name}', source.name || '');
    }

    open(): void {
        const d = this.data();
        if (!d) return;
        const form = overrideForm(d);
        this.hoursMode.set(form.hoursMode);
        this.zoneMode.set(form.zoneMode);
        this.timeZone.set(form.timeZone);
        this.rows.set(form.rows);
        this.editing.set(true);
    }

    close(): void {
        if (!this.saving()) this.editing.set(false);
    }

    async save(): Promise<void> {
        const request = this.request();
        if (typeof request === 'string' || this.saving()) return;
        if (request === null) return this.reset();
        await this.write(() => this.api.setLocationWorkingHours(this.locationId(), request), 'locationHours.saved');
    }

    async reset(): Promise<void> {
        if (!this.data()?.override) {
            this.editing.set(false);
            return;
        }
        await this.write(() => this.api.clearLocationWorkingHours(this.locationId()), 'locationHours.reset.done');
    }

    private async write(call: () => Promise<LocationWorkingHoursResponse>, doneKey: string): Promise<void> {
        this.saving.set(true);
        try {
            this.data.set(await call());
            this.editing.set(false);
            this.messages.add({ severity: 'success', summary: this.t()(doneKey) });
        } catch (err) {
            const e = err as { error?: { error?: string } | string; message?: string };
            const detail = (typeof e?.error === 'object' ? e.error?.error : e?.error) ?? e?.message;
            this.messages.add({ severity: 'error', summary: this.t()('workingHours.saveError'), detail });
        } finally {
            this.saving.set(false);
        }
    }

    private async load(id: string): Promise<void> {
        const request = ++this.loads;
        this.data.set(null);
        this.loadFailed.set(false);
        try {
            const data = await this.api.getLocationWorkingHours(id);
            if (request === this.loads) this.data.set(data);
        } catch {
            if (request === this.loads) this.loadFailed.set(true);
        }
    }
}
